import { useMemo } from 'react';

import type { BehandlingsvalgSammenlikningBlockDto, SamvalgstrinnBlockDto, SamvalgstrinnBlockMainContent } from '../../types/cms';

import Button from '@helsenorge/designsystem-react/components/Button';
import Icon from '@helsenorge/designsystem-react/components/Icon';
import Printer from '@helsenorge/designsystem-react/components/Icons/Printer';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';

import type { FasteTeksterDto } from '@helsenorge/core-cms/types/entities/cms.v1entities';
import type { VimeoSubtitleLanguageCodes } from '@helsenorge/core-cms/videospiller/constants';

import { contentMapper } from '../../content-helper/contentMapper';
import { getRelativePath } from '../../shared/url-utils';

import styles from './styles.module.scss';

interface Props {
  /** PortableText block for introduksjon */
  intro?: SamvalgstrinnBlockDto['introduction'];
  /** Blocker med hovedinnhold */
  mainContent?: SamvalgstrinnBlockDto['mainContent'];
  /** PortableText block for avsluttning */
  additionalContent?: SamvalgstrinnBlockDto['additionalContent'];
  showTreatmentLinks?: boolean;
  showComparisonAsTable?: boolean;
  showPrint?: boolean;
  fasteTekster: FasteTeksterDto;
  subtitleLanguageCode?: VimeoSubtitleLanguageCodes;
}

const isBehandlingsvalgSammenlikningBlock = (block: SamvalgstrinnBlockMainContent): block is BehandlingsvalgSammenlikningBlockDto =>
  block.type === 'BehandlingsvalgSammenlikningBlock';

const SamvalgtrinnBlock: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  intro,
  mainContent,
  additionalContent,
  showTreatmentLinks,
  showPrint,
  fasteTekster,
  subtitleLanguageCode,
}: React.PropsWithChildren<Props>) => {
  const { sammenlikningsBlockArray, updatedMainContent } = useMemo(() => {
    if (!mainContent) {
      return {
        sammenlikningsBlockArray: [] as BehandlingsvalgSammenlikningBlockDto[],
        updatedMainContent: [] as SamvalgstrinnBlockMainContent[],
      };
    }

    const filteredBlocks = mainContent.filter(isBehandlingsvalgSammenlikningBlock).map(mainContentBlock => ({
      ...mainContentBlock,
      treatmentUrl: getRelativePath(mainContentBlock.treatmentLink),
      treatmentUrlTitle: `${fasteTekster.moreAbout} ${mainContentBlock.treatmentLinkTitle}`,
    }));

    if (filteredBlocks.length <= 0) {
      return { sammenlikningsBlockArray: filteredBlocks, updatedMainContent: mainContent };
    }

    let insertedWrapper = false;
    const newMainContent = mainContent
      .map(mainContentBlock => {
        if (mainContentBlock.type === 'BehandlingsvalgSammenlikningBlock') {
          if (!insertedWrapper) {
            insertedWrapper = true;
            return { type: 'BehandlingsvalgSammenlikningWrapper' } as BehandlingsvalgSammenlikningBlockDto;
          }
          return null;
        }
        return mainContentBlock;
      })
      .filter(Boolean) as SamvalgstrinnBlockMainContent[];

    return { sammenlikningsBlockArray: filteredBlocks, updatedMainContent: newMainContent };
  }, [mainContent, fasteTekster]);

  return (
    <div>
      {intro &&
        intro.map((introContentBlock, index) => {
          return contentMapper(introContentBlock, index, [], showTreatmentLinks, undefined, subtitleLanguageCode);
        })}
      {updatedMainContent &&
        updatedMainContent.map((mainContentBlock, index) => {
          return contentMapper(mainContentBlock, index, sammenlikningsBlockArray, showTreatmentLinks, fasteTekster, subtitleLanguageCode);
        })}
      {additionalContent && (
        <div className={styles.blokkWrapper}>
          {additionalContent.map((additionalContentBlock, index) => {
            return contentMapper(additionalContentBlock, index, [], showTreatmentLinks, undefined, subtitleLanguageCode);
          })}
        </div>
      )}
      {showPrint && (
        <>
          <Spacer size={'2xl'} />
          <Button onClick={(): void => window.print()} variant={'outline'}>
            {fasteTekster.print}
            <Icon svgIcon={Printer} />
          </Button>
        </>
      )}
    </div>
  );
};

export default SamvalgtrinnBlock;
